import debugConsole from "./debugConsole";
import { captureCampaignAttribution } from "./campaignAttribution";
import { getQueryParams } from "./queryParams";
import { v4 as uuidv4 } from "uuid";

const { TOOL, isToolAllowed } = require("../../../../shared/cookiebotConsent");

// wrap the Mixpanel object so that we can initialize it on-demand

const isTestSession = () => {
  const isTest =
    getQueryParams()["is_test"] === "true" ||
    sessionStorage.getItem("soc_test") === "true";
  if (isTest) sessionStorage.setItem("soc_test", "true");
  return isTest;
};

const getSessionId = () => localStorage.getItem("sessionId");
const setSessionId = () => localStorage.setItem("sessionId", uuidv4());
const clearSessionId = () => localStorage.removeItem("sessionId");

const resetSessionId = () => {
  clearSessionId();
  setSessionId();
};

const trackerName = "soc-portal";
const logStyle = "color: #9c39e6";
let mixpanelPackage;
let mp;
let initialized = false;
let loadPromise;
let flushingQueue = false;
const queuedOperations = [];

const debug = (fn, first, ...rest) => fn(`%c${first}`, ...rest, logStyle);
const debugLog = (...args) => debug(debugConsole.log, ...args);
const debugGroup = (...args) => debug(debugConsole.groupCollapsed, ...args);
const logObject = (props) => {
  for (let key in props) {
    debugLog(`  ${key} = ${props[key]}`);
  }
};
const getCampaignAttribution = () => captureCampaignAttribution() || {};
const applyCampaignAttribution = (attribution = getCampaignAttribution()) => {
  if (attribution && Object.keys(attribution).length > 0) {
    mp.register(attribution);
  }
};

const flushQueuedOperations = () => {
  if (flushingQueue || !mp) return;
  flushingQueue = true;
  try {
    while (queuedOperations.length) {
      queuedOperations.shift()();
    }
  } finally {
    flushingQueue = false;
  }
};

const getMixpanelPackage = (module) => module.default || module;

const initializeLoadedMixpanel = () => {
  if (!mixpanelPackage) return false;

  if (!initialized) {
    initialized = trackerName in mixpanelPackage;
  }

  if (initialized && !mp) {
    mp = mixpanelPackage[trackerName];
  }

  if (!initialized) {
    mixpanelPackage.init(
      process.env.MIXPANEL_TOKEN,
      {
        record_sessions_percent: 5, // records 5% of all sessions,
        record_mask_text_selector: "", //unmask all text elements
        record_block_selector: "", //unmask images and videos
        persistence: "localStorage",
      },
      trackerName,
    );
    debugLog(`Mixpanel tracker ${trackerName} initialized`);
    initialized = true;
    mp = mixpanelPackage[trackerName];

    if (!getSessionId()) {
      setSessionId();
      mp.register({ session_id: getSessionId() });
    }
    applyCampaignAttribution();
  }

  return true;
};

const loadMixpanel = () => {
  if (!isToolAllowed(TOOL.MIXPANEL)) return false;

  if (initializeLoadedMixpanel()) return Promise.resolve(true);

  if (!loadPromise) {
    loadPromise = import(
      /* webpackChunkName: "analytics-mixpanel" */ "mixpanel-browser"
    )
      .then((module) => {
        mixpanelPackage = getMixpanelPackage(module);
        return initializeLoadedMixpanel();
      })
      .then((ready) => {
        if (ready) flushQueuedOperations();
        return ready;
      })
      .catch((error) => {
        queuedOperations.length = 0;
        debugConsole.error("Failed to load Mixpanel", error);
        return false;
      });
  }

  return loadPromise;
};

const enqueueMixpanelOperation = (operation) => {
  if (!isToolAllowed(TOOL.MIXPANEL)) return false;

  queuedOperations.push(operation);
  const maybePromise = loadMixpanel();

  if (!maybePromise) {
    queuedOperations.pop();
    return false;
  }

  if (maybePromise === true) {
    flushQueuedOperations();
    return true;
  }

  maybePromise.then((ready) => {
    if (ready) flushQueuedOperations();
  });

  return true;
};

const wrappedMixpanel = {
  identify: (id) => {
    debugLog(`mixpanel.identify id = ${id}`);
    enqueueMixpanelOperation(() => {
      if (isTestSession()) return;
      mp.register({ debtId: id, session_id: getSessionId(), ...getCampaignAttribution() });
      mp.people.set({ debtId: id });
      mp.identify(id);
    });
  },
  set: (props = {}, callback) => {
    const keys = Object.keys(props);

    if (keys.length === 1) {
      const key = keys[0];
      debugLog(`mixpanel.set ${key} = ${props[key]}`);
    } else {
      debugGroup(`mixpanel.set …`);
      logObject(props);
      debugConsole.groupEnd();
    }

    enqueueMixpanelOperation(() => {
      if (!isTestSession()) mp.people.set(props, callback);
    });
  },
  track: (eventName, properties = {}, options = {}, callback = () => {}) => {
    const campaignAttribution = getCampaignAttribution();
    const combinedProperties = {
      ...campaignAttribution,
      ...properties,
    };
    const hasProps = combinedProperties && Object.keys(combinedProperties).length > 0;
    const hasOptions = options && Object.keys(options).length > 0;

    const defaultOptions = {
      send_immediately: true,
    };

    const label = `mixpanel.track '${eventName}'`;

    if (hasProps || hasOptions) {
      debugGroup(`${label} …`);

      if (hasProps) {
        debugLog("properties:");
        logObject(combinedProperties);
      }

      if (hasOptions) {
        if (hasProps) {
          debugLog("");
        }

        debugLog("options:");
        logObject(options);
      }

      debugConsole.groupEnd();
    } else {
      debugLog(label);
    }

    const combinedOptions = {
      ...defaultOptions,
      options,
    };

    enqueueMixpanelOperation(() => {
      applyCampaignAttribution(campaignAttribution);
      if (!isTestSession()) {
      mp.track(eventName, combinedProperties, combinedOptions, callback);
      }
    });
  },
  track_links: (selector, eventName, props) => {
    debugLog(`mixpanel.track_links ${selector} ${eventName}`);
    const campaignAttribution = getCampaignAttribution();
    const combinedProps =
      typeof props === "function"
        ? (...args) => ({
            ...campaignAttribution,
            ...props(...args),
          })
        : {
            ...campaignAttribution,
            ...(props || {}),
          };
    return enqueueMixpanelOperation(() => {
      applyCampaignAttribution(campaignAttribution);
      if (!isTestSession()) {
        mixpanelPackage.track_links(selector, eventName, combinedProps);
      }
    });
  },
  track_pageview: (props, options) => {
    const eventName = options?.event_name || "";
    debugLog(`mixpanel.track_pageview ${eventName}`);
  },
  reset: () => {
    enqueueMixpanelOperation(() => {
      if (mp?.reset) mp.reset();
    });
  },
  registerSessionId: () => {
    enqueueMixpanelOperation(() => {
      mp.register({ session_id: getSessionId(), ...getCampaignAttribution() });
    });
  },
  registerCampaignAttribution: (attribution) => {
    enqueueMixpanelOperation(() => {
      applyCampaignAttribution(attribution);
    });
  },
};

const validUtmParams = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const updateUtmParams = (newParams = getQueryParams()) => {
  if (!window?.localStorage) return;
  if (!isToolAllowed(TOOL.MIXPANEL)) return;

  const mpStorageKey = Object.keys(localStorage).find((key) =>
    /^mp_[a-f0-9]+_mixpanel$/.test(key),
  );
  if (!mpStorageKey) return;

  const mpStorageValue = localStorage.getItem(mpStorageKey);
  if (!mpStorageValue) return;

  let mpData;

  try {
    // JSON.parse throws when given invalid JSON
    mpData = JSON.parse(mpStorageValue);
  } catch (_ignore) {
    debugConsole.err(
      "Malformed JSON in Mixpanel localStorage data",
      mpStorageValue,
    );
    return;
  }

  const utmKeys = Object.keys(newParams).filter((key) =>
    validUtmParams.includes(key),
  );
  if (utmKeys.length === 0) return;

  debugGroup(`UTM params updated …`);

  for (let key of utmKeys) {
    mpData[key] = newParams[key];
    debugLog(`  ${key} = ${newParams[key]}`);
  }

  debugConsole.groupEnd();

  localStorage.setItem(mpStorageKey, JSON.stringify(mpData));
};

export default wrappedMixpanel;
export { updateUtmParams, clearSessionId, getSessionId, resetSessionId };
